import {
  CreateGaiaObjectResponse,
  UpdateGaiaObjectRequest,
} from "../definitions";
import { GaiaObject } from "models/generateGaiaObjSchemas";

/**
 * Form configuration interface that controls UI behavior and visibility
 */
export interface FormConfiguration {
  // UI Visibility
  showMainForm: boolean;
  showTemplates: boolean;
  showFileUpload: boolean;
  showContacts: boolean;

  // Navigation
  enableNavigation: boolean;
}

/**
 * Form section enumeration for controlling visibility
 */
export enum FormSection {
  MAIN_FORM = "mainForm",
  CONTACTS = "contacts",
  TEMPLATES = "templates",
  FILE_UPLOAD = "fileUpload",
}

/**
 * Form data interface containing all form-related data
 */
export interface FormData {
  files?: File[];
  fileMetadata?: Array<{ file: File; documentType: string }>;
  template?: string;
  customAttributes?: Record<string, any>;
  transformedGaiaObject?: any;
  scanJobData?: {
    scanJob: {
      extensionType: string;
      inputS3Key: string;
      size: string;
      [key: string]: any;
    };
    [key: string]: any;
  };
}

/**
 * UI callbacks interface for form interaction handlers
 */
export interface UICallbacks {
  setDisableCreateEdit?: (disabled: boolean) => void;
  onWizardValidationSuccess?: (data: any) => void;
  onWizardValidationError?: (errors: any) => void;
}

/**
 * Transform functions interface for data transformation operations
 */
export interface TransformFunctions {
  buildGaiaObjectPropsFromFields: () => any;
  processGaiaObjectData: (data: any) => any;
  buildUpdateRequest: (data: any) => any;
}

/**
 * API operations interface with proper return types
 */
export interface APIOperations {
  createGaiaObject: (
    data: GaiaObject,
    context: SubmissionContext,
  ) => Promise<CreateGaiaObjectResponse>;
  updateGaiaObject: (
    request: UpdateGaiaObjectRequest,
    context: SubmissionContext,
  ) => Promise<GaiaObject>;
  uploadDocument: (
    file: File,
    gaiaObjectResponse: CreateGaiaObjectResponse,
    context: SubmissionContext,
    documentType: string,
  ) => Promise<void>;
  processDraftOperations: (
    responseShortId: string,
    context: SubmissionContext,
  ) => Promise<void>;
}

/**
 * Context object containing all dependencies needed for form submission
 * Grouped by concern for better organization
 */
export interface SubmissionContext {
  metadata: {
    type: string;
    shortId?: string;
    teamName: string;
    documentType: string;
  };
  formData: FormData;
  ui: UICallbacks;
  transforms: TransformFunctions;
  api: APIOperations;
  adapter: FormModeAdapter;
}

/**
 * Parameters for form initialization
 * All adapters receive the same comprehensive data structure,
 * but each adapter extracts only the fields it needs based on its mode
 */
export interface FormInitParams {
  // Required for all modes
  reset: any;
  setValue: any;

  // Mode-specific data (each adapter uses what it needs)
  defaultCountry?: string;
  transformedGaiaObject?: any;
  scanResultData?: any;
  scanJobData?: any;
  customAttributes?: any;
  uiContent?: any;
  currentUser?: any;
  localFormData?: any;
  shortId?: string;
}

/**
 * Main adapter interface that defines the contract for all form mode adapters
 * This interface provides a consistent way to handle different form modes
 * (create, edit, wizard, draft) through the Strategy Pattern
 */
export interface FormModeAdapter {
  // Data initialization methods
  initializeFormData?(params: FormInitParams): void;

  // UI Configuration methods
  getFormConfig(): FormConfiguration;
  shouldShowSection(section: FormSection): boolean;

  // Data loading configuration methods
  shouldLoadGaiaObject(): boolean;
  shouldLoadScanResult(): boolean;
  shouldLoadScanJob(): boolean;

  // Submission handling - each adapter implements its complete flow
  handleSubmit(data: any, context: SubmissionContext): Promise<void>;

  getGuestLoaderId(shortId?: string): string;
  getSubmitButtonText(): string;
  getDefaultFormValues(
    defaultCountry: string,
    defaultStatus: string,
  ): Record<string, any>;
  shouldPersistFormData(shortId?: string): boolean;
  getSuccessRedirectPath(type: string, shortId?: string): string;
  getCancelRedirectPath(type: string, shortId?: string): string;
}
